import React, { useEffect, useState, useRef } from "react";
import { Check, Info, WarningCircle, X, ArrowCounterClockwise } from "@phosphor-icons/react";
import { onToast, dismissToast } from "@/lib/toast";

const VARIANT_META = {
  info:    { color: "#1D4ED8", bg: "#EFF6FF", border: "#BFDBFE", Icon: Info },
  success: { color: "#065F46", bg: "#ECFDF5", border: "#6EE7B7", Icon: Check },
  error:   { color: "#991B1B", bg: "#FEF2F2", border: "#FECACA", Icon: WarningCircle },
};

function ToastItem({ toast }) {
  const meta = VARIANT_META[toast.variant] || VARIANT_META.info;
  const { Icon } = meta;
  const handleAction = () => {
    try { toast.action?.onClick(); }
    finally { dismissToast(toast.id); }
  };
  return (
    <div
      role="status"
      data-testid={`toast-${toast.id}`}
      style={{
        background: meta.bg,
        border: `1px solid ${meta.border}`,
        borderLeft: `4px solid ${meta.color}`,
        color: "#0F172A",
        borderRadius: 4,
        padding: "10px 12px",
        minWidth: 280,
        maxWidth: 420,
        boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        fontSize: 13,
        animation: "toast-in 180ms ease-out",
      }}
    >
      <Icon size={16} weight="bold" color={meta.color} style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, lineHeight: 1.4 }}>{toast.message}</div>
      {toast.action && (
        <button
          type="button"
          onClick={handleAction}
          data-testid={`toast-action-${toast.id}`}
          style={{
            background: meta.color,
            color: "#fff",
            border: "none",
            borderRadius: 3,
            padding: "5px 9px",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.06em",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 4,
            flexShrink: 0,
          }}
        >
          <ArrowCounterClockwise size={10} weight="bold" /> {toast.action.label || "UNDO"}
        </button>
      )}
      <button
        type="button"
        onClick={() => dismissToast(toast.id)}
        aria-label="Dismiss"
        data-testid={`toast-close-${toast.id}`}
        style={{ background: "transparent", border: "none", padding: 2, cursor: "pointer", color: "#64748B", flexShrink: 0 }}
      >
        <X size={12} weight="bold" />
      </button>
    </div>
  );
}

export default function ToastHost() {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  useEffect(() => {
    return onToast((event) => {
      if (event.kind === "add") {
        setToasts((prev) => [...prev, event.toast]);
        const id = event.toast.id;
        const t = setTimeout(() => {
          setToasts((prev) => prev.filter((x) => x.id !== id));
          timers.current.delete(id);
        }, event.toast.duration);
        timers.current.set(id, t);
      } else if (event.kind === "dismiss") {
        const t = timers.current.get(event.id);
        if (t) { clearTimeout(t); timers.current.delete(event.id); }
        setToasts((prev) => prev.filter((x) => x.id !== event.id));
      }
    });
  }, []);

  if (toasts.length === 0) return null;
  return (
    <>
      <style>{`@keyframes toast-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      <div
        data-testid="toast-host"
        style={{
          position: "fixed",
          bottom: 80,
          right: 18,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column-reverse",
          gap: 8,
          pointerEvents: "none",
        }}
      >
        {toasts.map((t) => (
          <div key={t.id} style={{ pointerEvents: "auto" }}>
            <ToastItem toast={t} />
          </div>
        ))}
      </div>
    </>
  );
}
